import React, { useEffect, useState } from 'react';
import {
  DeleteBlackIcon,
  MusicIcon,
  FileUploadIcon,
  VideoIcon
} from '@components/icons/svg-icon.tsx';
import { FileTypeEnum, MediaModel } from '@models/media-library.model.ts';
import { IconButton } from '@mui/material';
import AppImage from '@components/app-image/app-image.tsx';
import { getDownloadFileUrl } from '@api/file-upload-api.ts';

interface MediaPreviewProps {
  media: MediaModel | null | undefined;
  onRemove: (e: React.MouseEvent) => void;
  t: (key: string) => string;
  disableDelete?: boolean;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({
  media,
  onRemove,
  t,
  disableDelete
}) => {
  const [backgroundUrl, setBackgroundUrl] = useState<string>('');

  useEffect(() => {
    const fetchUrl = async () => {
      if (!media?.path) return;

      try {
        const { url } = await getDownloadFileUrl(media.path);
        setBackgroundUrl(url);
      } catch (err) {
        setBackgroundUrl('');
      }
    };

    fetchUrl();
  }, [media?.path]);

  if (!media) return null;

  const isImage = media.type === FileTypeEnum.Image;
  const isAudio = media.type === FileTypeEnum.Audio;
  const isDocument = media.type === FileTypeEnum.Document;
  const isVideo = media.type === FileTypeEnum.Video;

  const renderIconContent = () => {
    if (isAudio)
      return <MusicIcon className="!w-[74px] !h-[74px] text-gray-600 mb-2" />;
    if (isDocument)
      return (
        <FileUploadIcon className="!w-[74px] !h-[74px] text-gray-600 mb-2" />
      );
    if (isVideo)
      return <VideoIcon className="!w-[74px] !h-[74px] text-gray-600 mb-2" />;
    return null;
  };

  return (
    <div className="absolute h-full inset-0 rounded-lg overflow-hidden">
      {isImage && (
        <AppImage
          variant="image"
          filePath={media.path}
          imgClassName="!absolute !inset-0 !w-full !h-full object-cover rounded-lg"
          objectFit="cover"
          alt={media.name}
          disableModalClick
        />
      )}

      {(isAudio || isDocument || isVideo) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 min-w-0">
          {renderIconContent()}

          <span className="text-[rgba(113,142,191,1)] text-[10px] font-medium line-clamp-2 break-words break-all max-w-[90%]">
            {media.name}
          </span>

          {media.size && (
            <span className="text-[rgba(113,142,191,1)] font-normal text-[9px] opacity-50">
              {media.size} kb
            </span>
          )}
        </div>
      )}

      <div
        className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center"
        style={
          isImage
            ? {
                backgroundImage: backgroundUrl
                  ? `linear-gradient(0deg, rgba(128,128,128,0.8) 0%, rgba(128,128,128,0.8) 100%), url(${backgroundUrl})`
                  : 'rgba(248,249,253,1)',
                backgroundPosition: '50%',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat'
              }
            : { background: 'rgba(128,128,128,0.8)' }
        }
      >
        <div className="flex flex-col items-center gap-3">
          {!disableDelete && (
            <IconButton
              sx={{
                border: 'none',
                backgroundColor: 'var(--gray-11)',
                width: '30px',
                height: '30px',
                '&:hover': {
                  backgroundColor: 'var(--white)'
                }
              }}
              onClick={onRemove}
            >
              <DeleteBlackIcon className="w-4.25! h-4.25!" />
            </IconButton>
          )}
          <span className="text-(--white) font-medium text-base underline underline-offset-4">
            {t('replace_media')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MediaPreview;
